/**
 * Documents Service
 *
 * API calls for entity documents (purchase orders, goods receipts, transfers, etc.)
 * Documents are accessed via the parent entity routes.
 *
 * @see US-36.1 - Composants partagés documents
 */

import { apiClient } from '@/api/client';
import type { ApiSuccess, DocumentResponse, DocumentType } from '@bstock/shared';

export const documentsService = {
  /**
   * List documents attached to an entity
   * @param entityRoute - e.g. 'purchase-orders', 'goods-receipts', 'transfers'
   * @param entityId - UUID of the entity
   */
  async listByEntity(entityRoute: string, entityId: string): Promise<DocumentResponse[]> {
    const response = await apiClient.get<ApiSuccess<DocumentResponse[]>>(
      `/${entityRoute}/${entityId}/documents`
    );
    return response.data.data;
  },

  /**
   * Get a presigned download URL for a document
   * @param entityRoute - e.g. 'purchase-orders', 'goods-receipts', 'transfers'
   * @param entityId - UUID of the entity
   * @param documentId - UUID of the document
   */
  async getDownloadUrl(entityRoute: string, entityId: string, documentId: string): Promise<string> {
    const response = await apiClient.get<ApiSuccess<{ url: string }>>(
      `/${entityRoute}/${entityId}/documents/${documentId}/download`
    );
    return response.data.data.url;
  },

  /**
   * Upload a document to an entity
   * @param entityRoute - e.g. 'purchase-orders', 'goods-receipts', 'transfers'
   * @param entityId - UUID of the entity
   * @param file - File to upload
   * @param documentType - Optional document type (defaults to entity-specific default)
   */
  async uploadDocument(
    entityRoute: string,
    entityId: string,
    file: File,
    documentType?: DocumentType
  ): Promise<DocumentResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const params = documentType ? `?documentType=${documentType}` : '';

    const response = await apiClient.post<ApiSuccess<DocumentResponse>>(
      `/${entityRoute}/${entityId}/documents${params}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  /**
   * Delete a document from an entity
   * @param entityRoute - e.g. 'purchase-orders', 'goods-receipts', 'transfers'
   * @param entityId - UUID of the entity
   * @param documentId - UUID of the document
   */
  async deleteDocument(entityRoute: string, entityId: string, documentId: string): Promise<void> {
    await apiClient.delete(`/${entityRoute}/${entityId}/documents/${documentId}`);
  },
};
